import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Box, Button, Divider, Grid, Typography } from "@mui/material";

import {
  CancelBtn,
  CancelButton,
  DisciplineGridBody,
  GridBody,
  HorizontalDivider,
  ReadOnlyFieldLabel,
  TranslationText,
} from "../Style/DisciplineStyle";
import colors from "../../../styles/colors/colors";
import fontWeight from "../../../styles/mui/fontWeight";
import { DisciplineRecord, RationalTemplateFormDetails } from "../../../constants/Disciplines";
import ReadOnlyField from "../../../components/formComponents/ReadOnlyField";
import { DisciplineDetailsResponse } from "../../../models/Api/DisciplineDetails";
import { Discipline } from "../../../models/Authorization";
import { DisciplineDetails } from "../../../models/DisciplineDetails";
import { getValue } from "../../../utils";
import { DateFormats } from "../../../constants/Date";
import { formatDate } from "../../../utils/DateTime";

export interface PropsFromState {
  disciplineDetailsData: DisciplineDetailsResponse;
  setServiceDetails: Dispatch<SetStateAction<boolean>>;
  setDisciplineRecord: Dispatch<SetStateAction<Discipline>>;
  disciplineRecord: Discipline;
}
export interface PropsFromDispatch {
  getDisciplineDetails: (serviceRequestDisciplineId: number) => void;
}

type Props = PropsFromState & PropsFromDispatch;

const DisciplineServiceDetails: React.FC<Props> = ({
  disciplineDetailsData,
  getDisciplineDetails,
  setServiceDetails,
  setDisciplineRecord,
  disciplineRecord,
}: Props) => {
  const [disciplineDetails, setDisciplineDetails] =
    useState<DisciplineDetails>();
  const { response } = disciplineDetailsData;
  const { serviceRequestDisciplineId } = disciplineRecord;

  const DenialRationale =
    !!disciplineDetails?.rationale ||
    !!disciplineDetails?.resourceDescription ||
    !!disciplineDetails?.medicaidRationale;

  const DenialRationaleTranslation =
    !!disciplineDetails?.otherRationale ||
    !!disciplineDetails?.otherResourceDescription ||
    !!disciplineDetails?.otherMedicaidRationale;

  const hasDenialRationale = getValue(disciplineDetails, "serviceStatus") === "Denied" || getValue(disciplineDetails, "serviceStatus") === "Partially Approved";
  const hasDenialAndMedicaid =
  (hasDenialRationale &&
    (DenialRationale || DenialRationaleTranslation)) ||
  !!disciplineDetails?.medicaidRationale ||
  !!disciplineDetails?.otherMedicaidRationale;
  
  useEffect(() => {
    if (serviceRequestDisciplineId !== -1) {
      getDisciplineDetails(serviceRequestDisciplineId);
    }
  }, [serviceRequestDisciplineId]);

  useEffect(() => {
    if (response) {
      setDisciplineDetails(response);
    }
  }, [response]);

  return (
    <Grid container sx={DisciplineGridBody}>
      <Grid size={12}>
        <Grid container sx={CancelButton}>
          <Button
            sx={CancelBtn}
            variant="outlined"
            onClick={() => {
              setServiceDetails(false);
              setDisciplineRecord(DisciplineRecord);
            }}
            data-testid="discipline-line-cancel-top"
          >
            CANCEL
          </Button>
        </Grid>
        <Grid container>
          <Grid size={8} sx={GridBody} flexGrow={1}>
            <Grid container>
              <Grid size={3} p={`0.5rem`}>
                <ReadOnlyField
                  label="Type:"
                  value={getValue(disciplineDetails, "serviceRequestTypeName")}
                />
              </Grid>
              <Grid size={9} p={`0.5rem`}>
                <ReadOnlyField
                  label="Discipline:"
                  value={getValue(disciplineDetails, "disciplineName")}
                />
              </Grid>
              <Grid size={12} p={`0.5rem`}>
                <ReadOnlyField
                  label="Date/Time Request Received:"
                  value={formatDate(
                    getValue(disciplineDetails, "requestDateTime"),
                    DateFormats.MM_DD_YYYY_comma_hh_mm_ss_a,
                  )}
                />
              </Grid>
              <Grid size={12} p={`0.5rem`}>
                <ReadOnlyField
                  label="Urgency:"
                  value={getValue(disciplineDetails, "urgencyStatus")}
                />
              </Grid>
              <Grid size={3} p={`0.5rem`}>
                <ReadOnlyField
                  label="Service Status:"
                  value={getValue(disciplineDetails, "serviceStatus")}
                />
              </Grid>
              <Grid size={9} p={`0.5rem`}>
                <ReadOnlyField
                  label="Service Status Reason:"
                  value={getValue(disciplineDetails, "serviceStatusReason")}
                />
              </Grid>
              <Grid size={3} p={`0.5rem`}>
                <ReadOnlyField
                  label="Requested Visits:"
                  value={getValue(disciplineDetails, "requestedVisits")}
                />
              </Grid>
              <Grid size={3} p={`0.5rem`}>
                <ReadOnlyField
                  label="Approved Visits:"
                  value={getValue(disciplineDetails, "approvedVisits")}
                />
              </Grid>
              <Grid size={6} p={`0.5rem`}>
                <ReadOnlyField
                  label="Denied Visits:"
                  value={getValue(disciplineDetails, "deniedVisits")}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={0.2}></Grid>
          <Grid size={3} sx={GridBody}>
            <Grid container>
              <Grid size={6}>
                <ReadOnlyField
                  label="Created By:"
                  value={getValue(disciplineDetails, "createdBy")}
                />
              </Grid>
              <Grid size={6}>
                <ReadOnlyField
                  label="Created On:"
                  value={formatDate(
                    getValue(disciplineDetails, "dateCreated"),
                    DateFormats.MM_DD_YYYY,
                  )}
                />
              </Grid>
              <Grid size={6}>
                <ReadOnlyField
                  label="Effective From:"
                  value={formatDate(
                    getValue(disciplineDetails, "effectiveFromDate"),
                    DateFormats.MM_DD_YYYY,
                  )}
                />
              </Grid>
              <Grid size={6}>
                <ReadOnlyField
                  label="Effective Through:"
                  value={formatDate(
                    getValue(disciplineDetails, "effectiveThroughDate"),
                    DateFormats.MM_DD_YYYY,
                  )}
                />
              </Grid>
              <Grid size={12}>
                <ReadOnlyField
                  label="Requested By:"
                  value={getValue(disciplineDetails, "requestedBy")}
                />
              </Grid>
              <Grid size={12}>
                <ReadOnlyField
                  label="Received Methods:"
                  value={getValue(disciplineDetails, "receivedMethodName")}
                />
              </Grid>
              <Grid size={6}>
                <ReadOnlyField
                  label="Decided By:"
                  value={getValue(disciplineDetails, "decidedBy")}
                />
              </Grid>
              <Grid size={6}>
                <ReadOnlyField
                  label="Decided On:"
                  value={formatDate(
                    getValue(disciplineDetails, "decidedDateTime"),
                    DateFormats.MM_DD_YYYY,
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {hasDenialAndMedicaid && (
          <Grid container sx={GridBody}>
                {((hasDenialRationale && DenialRationale) || !!disciplineDetails?.medicaidRationale) && (
              <Grid size={11.5} sx={TranslationText} pb={"0.5rem"}>
                    <Box>
                      <Typography
                        variant="h2"
                        color={colors.fonts[2]}
                        fontWeight={fontWeight.Weight[6]}
                      >
                        Denial Rationale
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {hasDenialRationale && disciplineDetails?.rationale && (
                  <>
                <Grid size={11.5} pt={"1rem"}>
                      <ReadOnlyField
                        label="Description:"
                        value={getValue(disciplineDetails, "rationale")}
                      />
                    </Grid>
                    <Divider sx={HorizontalDivider} />
                  </>
                )}
                {hasDenialRationale &&  disciplineDetails?.resourceDescription && (
                  <>
                    <Grid size={11.5}>
                      <Box sx={ReadOnlyFieldLabel}>
                        <Typography
                          fontWeight={fontWeight.Weight[3]}
                          color={colors.fonts[2]}
                        >
                          {RationalTemplateFormDetails.title}
                        </Typography>
                      </Box>
                    </Grid>
                <Grid size={11.5} pt={"1rem"}>
                      <ReadOnlyField
                        label="Description:"
                    value={getValue(disciplineDetails, "resourceDescription")}
                  />
                </Grid>
                <Divider sx={HorizontalDivider} />
              </>
            )}
            {disciplineDetails?.medicaidRationale && (
              <>
                <Grid size={11.5}>
                  <Box sx={ReadOnlyFieldLabel}>
                    <Typography
                      fontWeight={fontWeight.Weight[3]}
                      color={colors.fonts[2]}
                    >
                      {RationalTemplateFormDetails.medicaid}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={11.5} pt={"1rem"}>
                  <ReadOnlyField
                    label="Description:"
                    value={getValue(disciplineDetails, "medicaidRationale")}
                      />
                    </Grid>
                    <Divider sx={HorizontalDivider} />
                  </>
                )}
                {((hasDenialRationale && DenialRationaleTranslation) || !!disciplineDetails?.otherMedicaidRationale) && (
              <Grid size={11.5} sx={TranslationText} pb={"0.5rem"}>
                    <Box>
                      <Typography
                        variant="h2"
                        color={colors.fonts[2]}
                        fontWeight={fontWeight.Weight[6]}
                      >
                        Denial Rationale Translation
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {hasDenialRationale && disciplineDetails?.otherRationale && (
                  <>
                <Grid size={11.5} pt={"1rem"}>
                      <ReadOnlyField
                        label="Description:"
                        value={getValue(disciplineDetails, "otherRationale")}
                      />
                    </Grid>
                    <Divider sx={HorizontalDivider} />
                  </>
                )}
                {hasDenialRationale && disciplineDetails?.otherResourceDescription && (
                  <>
                    <Grid size={11.5}>
                      <Box sx={ReadOnlyFieldLabel}>
                        <Typography
                          fontWeight={fontWeight.Weight[3]}
                          color={colors.fonts[2]}
                        >
                          {RationalTemplateFormDetails.title}
                        </Typography>
                      </Box>
                    </Grid>
                <Grid size={11.5} pt={"1rem"}>
                      <ReadOnlyField
                        label="Description:"
                        value={getValue(
                          disciplineDetails,
                          "otherResourceDescription",
                        )}
                      />
                    </Grid>
                <Divider sx={HorizontalDivider} />
              </>
            )}

            {disciplineDetails?.otherMedicaidRationale && (
              <>
                <Grid size={11.5}>
                  <Box sx={ReadOnlyFieldLabel}>
                    <Typography
                      fontWeight={fontWeight.Weight[3]}
                      color={colors.fonts[2]}
                    >
                      {RationalTemplateFormDetails.othermedicaid}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={11.5} pt={"1rem"}>
                  <ReadOnlyField
                    label="Description:"
                    value={getValue(
                      disciplineDetails,
                      "otherMedicaidRationale",
                    )}
                  />
                </Grid>
                <Divider sx={HorizontalDivider} />
              </>
            )}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default DisciplineServiceDetails;
